import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { previewToDOM } from '@office-kit/docx-preview';
import { captureException } from '@sentry/react';
import { createPortal } from 'react-dom';
import { TbFileTypeDocx } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import {
    AttachmentDestination,
    CloudAttachment,
    CloudStorageConnection,
    getCloudAttachmentContent,
    getDocumentOntologyCatalog,
} from '@/services';
import { EmptyView } from '@/shared/components/layouts/views/EmptySection';
import LoadingContent from '@/shared/components/layouts/views/LoadingContent';

import type { AttachmentDocumentReview } from '../AttachmentPreview';
import DocumentConceptOverlay from '../AttachmentRedactionEditor/DocumentConceptOverlay';
import {
    DocumentConceptReference,
    DocumentIntelligence,
    EMPTY_DOCUMENT_INTELLIGENCE,
} from '../AttachmentRedactionEditor/documentIntelligence';
import { buildRenderedDocxIntelligence, RenderedDocxPage } from './docxLayout';

import './index.css';

interface DocxAttachmentPreviewProps {
    attachment: CloudAttachment;
    connection: CloudStorageConnection;
    destination?: AttachmentDestination;
    onReviewChange?: (review?: AttachmentDocumentReview) => void;
}

const waitForLayout = () =>
    new Promise<void>((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));

const DocxAttachmentPreview: FC<DocxAttachmentPreviewProps> = ({
    attachment,
    connection,
    destination,
    onReviewChange,
}) => {
    const { token } = useAntDesignTheme();
    const containerRef = useRef<HTMLDivElement>(null);
    const documentInputRef = useRef({ attachment, connection, destination });
    const pagesRef = useRef<RenderedDocxPage[]>([]);
    const [activeReferenceId, setActiveReferenceId] = useState<string>();
    const [error, setError] = useState<string>();
    const [intelligence, setIntelligence] = useState<DocumentIntelligence>(EMPTY_DOCUMENT_INTELLIGENCE);
    const [loading, setLoading] = useState(true);
    const [pages, setPages] = useState<RenderedDocxPage[]>([]);

    useEffect(() => {
        documentInputRef.current = { attachment, connection, destination };
    }, [attachment, connection, destination]);

    const selectReference = useCallback((reference: DocumentConceptReference) => {
        setActiveReferenceId(reference.id);
        const container = containerRef.current?.parentElement;
        const page = pagesRef.current[reference.page - 1]?.element;

        if (!container || !page) {
            return;
        }

        const containerBounds = container.getBoundingClientRect();
        const pageBounds = page.getBoundingClientRect();
        const referenceTop = pageBounds.top + reference.y * pageBounds.height;

        container.scrollTo({
            behavior: 'smooth',
            top: Math.max(container.scrollTop + referenceTop - containerBounds.top - containerBounds.height / 2, 0),
        });
    }, []);

    useEffect(() => {
        const controller = new AbortController();
        const container = containerRef.current;
        const documentInput = documentInputRef.current;
        let dispose: (() => void) | undefined;

        if (!container) {
            return;
        }

        onReviewChange?.(undefined);

        void getCloudAttachmentContent({ ...documentInput, signal: controller.signal })
            .then(async (content) => {
                if (controller.signal.aborted) {
                    return;
                }

                const handle = await previewToDOM(content, container, {
                    breakPages: true,
                    classPrefix: 'attachment-docx-',
                    inWrapper: true,
                    renderFonts: true,
                });

                if (controller.signal.aborted) {
                    handle.dispose();
                    return;
                }

                dispose = handle.dispose;
                await document.fonts?.ready;
                await waitForLayout();

                if (controller.signal.aborted) {
                    return;
                }

                const ontology = await getDocumentOntologyCatalog();

                if (controller.signal.aborted) {
                    return;
                }

                const result = buildRenderedDocxIntelligence(container, ontology, {
                    mimeType: documentInput.attachment.mimeType,
                    name: documentInput.attachment.name,
                });

                pagesRef.current = result.pages;
                setIntelligence(result.intelligence);
                setPages(result.pages);
                onReviewChange?.({
                    document: result.document,
                    intelligence: result.intelligence,
                    loading: false,
                    onReferenceSelect: selectReference,
                    textExtractionSupported: true,
                });
            })
            .catch((cause: unknown) => {
                if (!controller.signal.aborted) {
                    captureException(cause);
                    setError('The DOCX attachment could not be rendered securely in this browser.');
                }
            })
            .finally(() => {
                if (!controller.signal.aborted) {
                    setLoading(false);
                }
            });

        return () => {
            controller.abort();
            dispose?.();
            pagesRef.current = [];
            onReviewChange?.(undefined);
        };
    }, [
        attachment.id,
        attachment.mimeType,
        connection.id,
        destination?.listName,
        destination?.recordName,
        destination?.resourceId,
        destination?.resourceName,
        destination?.resourceType,
        onReviewChange,
        selectReference,
    ]);

    return (
        <div className='attachment-docx-preview' style={{ background: token.colorFillQuaternary }}>
            {loading ? (
                <div style={{ inset: 0, position: 'absolute', zIndex: 10 }}>
                    <LoadingContent />
                </div>
            ) : null}
            {error ? (
                <EmptyView
                    description={error}
                    height='100%'
                    icon={<TbFileTypeDocx style={{ color: token.colorTextTertiary }} />}
                    label='DOCX Preview Unavailable'
                />
            ) : null}
            <div
                ref={containerRef}
                style={{ display: error ? 'none' : 'block', visibility: loading ? 'hidden' : 'visible' }}
            />
            {pages.flatMap((page) =>
                createPortal(
                    <DocumentConceptOverlay
                        key={`docx-concepts:${page.page}`}
                        activeReferenceId={activeReferenceId}
                        currentPage={page.page}
                        enabled
                        intelligence={intelligence}
                        onReferenceSelect={selectReference}
                    />,
                    page.element,
                ),
            )}
        </div>
    );
};

export default DocxAttachmentPreview;
